/**
 * §P.3 · Las tarifas del club.
 *
 * `BFF_BO.md` §7.3 da seis reglas y esta pantalla las cumple todas. Las tres que
 * cambian lo que se ve:
 *
 * **No se reordena.** Viene ordenada por importe, «que es como se lee una lista de
 * precios». Ordenarla alfabéticamente sería deshacer una decisión del servidor.
 *
 * **«Retirar», no «Eliminar».** El `DELETE` devuelve la tarifa con `active: false`,
 * y la palabra importa: los recibos de años anteriores siguen apuntando a ella. Y
 * con cuotas activas es `409 fee_plan_in_use` **diciendo cuántas**, que es el
 * número que hay que enseñar para que el club sepa qué le falta por hacer.
 *
 * **Subir el precio no cambia lo ya facturado**, y se dice en la pantalla: es la
 * duda que frena al club. «Esto afectará a los próximos recibos; los ya emitidos
 * no cambian.»
 *
 * Y el `If-Match`, que aquí importa más que en ninguna otra pantalla: el dueño y la
 * administrativa editando el mismo precio la misma semana de septiembre es el caso
 * normal, no el raro.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import {
  camposQueCambiaron,
  esConflictoDeEdicion,
  etagDe,
} from '@/shared/api/concurrencia';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { euros } from '@/shared/ui/formato';
import {
  aCentimos,
  aEuros,
  etiquetaDePeriodo,
  PERIODICIDADES,
  type Periodicidad,
  type Tarifa,
} from '@/club/cuotas/tipos';

interface Campos {
  name: string;
  importe: string;
  period: Periodicidad;
  sessions_per_week: string;
}

const ETIQUETAS: Record<string, string> = {
  name: 'el nombre',
  amount_cents: 'el importe',
  period: 'la periodicidad',
  sessions_per_week: 'las sesiones por semana',
  active: 'si está retirada',
};

const desde = (t: Tarifa | null): Campos => ({
  name: t?.name ?? '',
  importe: t ? aEuros(t.amount_cents) : '',
  period: (t?.period as Periodicidad) ?? 'monthly',
  sessions_per_week: t?.sessions_per_week?.toString() ?? '',
});

export function Tarifas({
  club,
  puedeEditar,
  alVerCuotasDe,
  empezarCreando = false,
  alCrear,
}: {
  club: string;
  puedeEditar: boolean;
  /** Ir a las cuotas filtradas por esta tarifa: es lo que hay que hacer para retirarla. */
  alVerCuotasDe: (tarifaId: string) => void;
  /** Abrir directamente el alta (`?nuevo=1`). */
  empezarCreando?: boolean;
  /** Al crear una tarifa, volver adonde se estaba (`?volver=`, 03-11-2026). */
  alCrear?: (() => void) | undefined;
}) {
  const cache = useQueryClient();
  const [conRetiradas, setConRetiradas] = useState(false);
  const [editando, setEditando] = useState<Tarifa | null | undefined>(
    empezarCreando ? null : undefined
  );
  const [campos, setCampos] = useState<Campos>(() => desde(null));
  const [etagVivo, setEtagVivo] = useState<string | null>(null);
  const [tocados, setTocados] = useState<Set<string>>(new Set());
  const [problema, setProblema] = useState<Problema | null>(null);
  const [conflicto, setConflicto] = useState<readonly string[] | null>(null);
  const [enUso, setEnUso] = useState<{ id: string; problema: Problema } | null>(
    null
  );

  const tarifas = useQuery({
    queryKey: ['tarifas', club, conRetiradas],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club })['fee-plans'].get({
        query: conRetiradas ? { include_retired: true } : {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Tarifa[];
    },
  });

  /**
   * Abrir para editar **pide la tarifa**, no usa la de la lista.
   *
   * No es una llamada de más: el `PATCH` exige `If-Match` y **la lista no manda
   * `ETag`** —un `ETag` de colección no sirve como precondición de un elemento—.
   * Por eso existe `GET /fee-plans/{id}`, que se añadió justo para esto.
   */
  const abrir = async (t: Tarifa | null) => {
    setEditando(t);
    setCampos(desde(t));
    setTocados(new Set());
    setProblema(null);
    setConflicto(null);
    setEnUso(null);
    setEtagVivo(null);

    if (!t) return;

    const r = await api.v1
      .clubs({ club })
      ['fee-plans']({ feePlanId: t.id })
      .get();

    if (r.error) {
      setProblema(aProblema(r.error));
      return;
    }

    setEtagVivo(etagDe(r));
    // Y se rellena con lo que dice el servidor ahora, no con lo que había en la
    // lista: si alguien acaba de cambiar el precio, se edita sobre el nuevo.
    setCampos(desde(r.data as unknown as Tarifa));
  };

  const cerrar = () => setEditando(undefined);

  const guardar = useMutation({
    mutationFn: async () => {
      const centimos = aCentimos(campos.importe);
      if (centimos === null) {
        throw new ErrorDeApi({
          title: 'El importe no se entiende',
          status: 400,
          detail:
            'Escríbelo en euros, con dos decimales como mucho: 25, 25,50 o 25.50.',
          errors: [{ field: 'amount_cents', message: 'No es un importe' }],
        });
      }

      const sesiones = Number.parseInt(campos.sessions_per_week.trim(), 10);
      const comun = {
        name: campos.name.trim(),
        amount_cents: centimos,
        period: campos.period,
        sessions_per_week: Number.isFinite(sesiones) ? sesiones : null,
      };

      if (editando === null) {
        const r = await api.v1.clubs({ club })['fee-plans'].post(comun);
        if (r.error) throw new ErrorDeApi(aProblema(r.error));
        return r.data as unknown as Tarifa;
      }

      const r = await api.v1
        .clubs({ club })
        ['fee-plans']({ feePlanId: editando!.id })
        .patch(comun, {
          headers: etagVivo ? { 'if-match': etagVivo } : {},
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as Tarifa;
    },
    onSuccess: () => {
      const eraNueva = editando === null;
      void cache.invalidateQueries({ queryKey: ['tarifas', club] });
      void cache.invalidateQueries({ queryKey: ['hoy', club] });
      cerrar();
      // Venía de una cuota a la que le faltaba la tarifa: vuelve a ella.
      if (eraNueva) alCrear?.();
    },
    onError: async (error) => {
      const p = aProblema(error);
      if (!esConflictoDeEdicion(p) || !editando) {
        setProblema(p);
        return;
      }

      // El precio actual y su `ETag` nuevo, para enseñar qué cambió y dejar
      // reintentar sin volver a la lista.
      const r = await api.v1
        .clubs({ club })
        ['fee-plans']({ feePlanId: editando.id })
        .get();
      if (r.error || !r.data) {
        setProblema(p);
        return;
      }
      const actual = r.data as unknown as Tarifa;
      setEtagVivo(etagDe(r));
      setConflicto(
        camposQueCambiaron(
          editando as unknown as Record<string, unknown>,
          actual as unknown as Record<string, unknown>,
          [...tocados]
        )
      );
    },
  });

  const retirar = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1
        .clubs({ club })
        ['fee-plans']({ feePlanId: id })
        .delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setEnUso(null);
      void cache.invalidateQueries({ queryKey: ['tarifas', club] });
    },
    onError: (e, id) => {
      const p = aProblema(e);
      if (p.code === 'fee_plan_in_use') setEnUso({ id, problema: p });
      else setProblema(p);
    },
  });

  // ── Formulario ───────────────────────────────────────────────────────────
  if (editando !== undefined) {
    const cambiar = (campo: keyof Campos, valor: string) => {
      setCampos((c) => ({ ...c, [campo]: valor }));
      setTocados((t) => new Set(t).add(campo));
    };

    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          guardar.mutate();
        }}
      >
        <h1>{editando ? 'Editar tarifa' : 'Nueva tarifa'}</h1>

        {conflicto && (
          <div className="aviso" role="status">
            <strong>
              Alguien ha cambiado esta tarifa mientras la editabas.
            </strong>{' '}
            {conflicto.length > 0
              ? `Cambió ${conflicto.map((c) => ETIQUETAS[c] ?? c).join(', ')}. Lo tuyo sigue escrito abajo.`
              : 'Los cambios no afectan a lo que estás tocando.'}
          </div>
        )}

        <div className="rejilla">
          <label>
            Nombre
            <input
              required
              maxLength={60}
              placeholder="Infantil"
              value={campos.name}
              onChange={(e) => cambiar('name', e.target.value)}
            />
          </label>

          <label>
            Importe
            {/* Se teclea en euros y se manda en céntimos enteros. */}
            <input
              required
              inputMode="decimal"
              placeholder="25,00"
              value={campos.importe}
              onChange={(e) => cambiar('importe', e.target.value)}
            />
          </label>

          <label>
            Cada cuánto se cobra
            <select
              value={campos.period}
              onChange={(e) => cambiar('period', e.target.value)}
            >
              {PERIODICIDADES.map((p) => (
                <option key={p.valor} value={p.valor}>
                  {p.etiqueta}
                </option>
              ))}
            </select>
          </label>

          <label>
            Sesiones por semana
            <input
              type="number"
              min={1}
              max={14}
              placeholder="Opcional"
              value={campos.sessions_per_week}
              onChange={(e) => cambiar('sessions_per_week', e.target.value)}
            />
          </label>
        </div>

        {/* La duda que frena al club, contestada antes de que la haga. */}
        {editando && (
          <div className="nota">
            <strong>Esto afectará a los próximos recibos.</strong> Los ya
            emitidos no cambian: guardan el importe con el que se emitieron.
          </div>
        )}

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={guardar.isPending}>
            {guardar.isPending
              ? 'Guardando…'
              : editando
                ? 'Guardar la tarifa'
                : 'Crear la tarifa'}
          </button>
          <button
            type="button"
            className="btn-plano"
            onClick={cerrar}
            disabled={guardar.isPending}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── Lista ────────────────────────────────────────────────────────────────
  return (
    <section>
      <header className="titulo-fila">
        <h1>Tarifas</h1>
        {puedeEditar && (
          <button type="button" onClick={() => void abrir(null)}>
            Nueva tarifa
          </button>
        )}
      </header>

      <p className="tenue">
        Cuánto cuesta cada tipo de cuota. De aquí sale el importe de los
        recibos.
      </p>

      <label className="interruptor">
        <input
          type="checkbox"
          checked={conRetiradas}
          onChange={(e) => setConRetiradas(e.target.checked)}
        />
        Enseñar también las retiradas
      </label>

      {tarifas.isPending && <Cargando filas={4} etiqueta="Cargando tarifas" />}

      {tarifas.isError && (
        <PanelError
          problema={aProblema(tarifas.error)}
          reintentar={() => void tarifas.refetch()}
        />
      )}

      {problema && !enUso && <PanelError problema={problema} />}

      {/*
        `409 fee_plan_in_use` con el número de cuotas, y el camino para resolverlo:
        ir a esas cuotas. Un error a secas dejaría al club sin saber qué hacer.
      */}
      {enUso && (
        <div className="aviso" role="status">
          <strong>{enUso.problema.title}</strong> {enUso.problema.detail}
          <div className="hueco-acciones">
            <button type="button" onClick={() => alVerCuotasDe(enUso.id)}>
              Ver esas cuotas
            </button>
            <button
              type="button"
              className="btn-plano"
              onClick={() => setEnUso(null)}
            >
              Dejarlo
            </button>
          </div>
        </div>
      )}

      {tarifas.data?.length === 0 && (
        <Vacio
          titulo="Todavía no hay tarifas"
          explicacion="Una tarifa es un precio con nombre: «Infantil, 25 € al mes». Sin tarifas no se puede dar de alta una cuota, y sin cuotas no hay recibos."
          {...(puedeEditar
            ? {
                accion: (
                  <button type="button" onClick={() => void abrir(null)}>
                    Crear la primera tarifa
                  </button>
                ),
              }
            : {})}
        />
      )}

      {tarifas.data && tarifas.data.length > 0 && (
        <div className="tabla-marco">
          <table className="tabla">
            <thead>
              <tr>
                <th>Tarifa</th>
                <th className="num">Importe</th>
                <th>Cada</th>
                <th className="num">Sesiones</th>
                {puedeEditar && <th />}
              </tr>
            </thead>
            {/* Sin reordenar: viene por importe, que es como se lee una lista de precios. */}
            <tbody>
              {tarifas.data.map((t) => (
                <tr key={t.id} className={t.active ? undefined : 'fila-fuera'}>
                  <td>
                    {t.name}
                    {!t.active && (
                      <span className="pastilla pastilla-left">Retirada</span>
                    )}
                  </td>
                  <td className="num">{euros(t.amount_cents)}</td>
                  <td className="tenue">{etiquetaDePeriodo(t.period)}</td>
                  <td className="num tenue">{t.sessions_per_week ?? '—'}</td>
                  {puedeEditar && (
                    <td>
                      <div className="tutor-acciones" style={{ margin: 0 }}>
                        <button
                          type="button"
                          className="btn-plano"
                          onClick={() => void abrir(t)}
                        >
                          Editar
                        </button>
                        {t.active && (
                          <button
                            type="button"
                            className="btn-plano"
                            disabled={retirar.isPending}
                            onClick={() => retirar.mutate(t.id)}
                          >
                            Retirar
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
